import { useState } from 'react'

import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Textarea } from 'core-components/library/inputs'
import { IModalContent, ModalDialog } from 'core-components/library/modalDialog'

import { PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH } from '../../config/justification'
import { isPlanReviewJustificationValid } from '../../lib/isPlanReviewJustificationValid'
import { IPlanReviewJustificationTarget } from '../../model/types'

import styles from './JustificationDialog.module.scss'

interface IJustificationDialogProps {
  target: IPlanReviewJustificationTarget | null
  value?: string
  isSaving: boolean
  saveError: string | null
  onClose: () => void
  onSave: (value: string) => Promise<boolean>
}

interface IJustificationDialogContentProps extends IModalContent {
  target: IPlanReviewJustificationTarget
  initialValue: string
  isSaving: boolean
  saveError: string | null
  onSave: (value: string) => Promise<boolean>
}

const JustificationDialogContent = ({
  target,
  initialValue,
  isSaving,
  saveError,
  onSave,
  closeModal,
}: IJustificationDialogContentProps) => {
  const [value, setValue] = useState(initialValue)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const normalizedValue = value.trim()
  const justificationLength = normalizedValue.length
  const isValid = isPlanReviewJustificationValid(value)
  const handleSave = async () => {
    if (!isValid || isSaving) {
      return
    }

    setHasSubmitted(true)
    if (await onSave(normalizedValue)) {
      closeModal()
    }
  }

  return (
    <div className={styles.content}>
      <div className={styles.context}>
        <Translation text={target.label} />
      </div>
      <Textarea
        label="Justification"
        value={value}
        onChange={setValue}
        rows={5}
        resizable="vertical"
        autoFocus
        disabled={isSaving}
        __testId="plan-review-justification-input"
      />

      <div className={styles.footer}>
        {hasSubmitted && saveError ? (
          <span className={styles.saveError} role="alert">
            {saveError}
          </span>
        ) : (
          <span className={styles.requirement} aria-live="polite">
            Minimum {PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH} characters: {justificationLength}/
            {PLAN_REVIEW_JUSTIFICATION_MIN_LENGTH}
          </span>
        )}
        <div className={styles.footerActions}>
          <Button color="grey" variant="outlined" disabled={isSaving} onClick={closeModal}>
            Cancel
          </Button>
          <Button disabled={!isValid || isSaving} onClick={() => void handleSave()}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export const JustificationDialog = ({
  target,
  value = '',
  isSaving,
  saveError,
  onClose,
  onSave,
}: IJustificationDialogProps) => {
  if (!target) {
    return null
  }

  return (
    <ModalDialog
      show
      size="xs"
      closeOnOutsideClick={false}
      onClose={() => {
        if (!isSaving) {
          onClose()
        }
      }}
      header={() => <span>Enter justification</span>}
      content={JustificationDialogContent}
      contentProps={{
        target,
        initialValue: value,
        isSaving,
        saveError,
        onSave,
      }}
    />
  )
}
